import React, { useEffect, useRef } from 'react';
import { Typography, Box, Button } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import Menu from '../../components/Menu';
import Footer from '../../components/Footer';
import ChatAssistant from '../../components/ChatAssistant';
import SectionHeading from '../../components/SectionHeading';
import { ease } from '../../utils/animations';
import { colors } from '../../theme';
import { track } from '../../utils/analytics';
import useSectionViews from '../../hooks/useSectionViews';
import ServicesSection from './ServicesSection';
import FilmSection from './FilmSection';
import WorkSection from './WorkSection';
import Testimonials from './Testimonials';
import AboutSection from './AboutSection';

// Phones and data-saver users get the poster first and the loop on demand.
const LIGHT_MEDIA = typeof window !== 'undefined' && (
    window.matchMedia('(max-width: 899px)').matches ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    navigator.connection?.saveData === true
);

// Phones get a separate 9:16 cut of the same loop, framed shot by shot, since
// cover-cropping the 2.4:1 version loses the subject in most shots. Decided
// once at load; a rotation mid-visit keeps whichever file is playing.
const PHONE = typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;
const HERO_VIDEO = PHONE ? '/media/hero-loop-8-portrait.mp4' : '/media/hero-loop-8.mp4';
const HERO_POSTER = PHONE ? '/media/hero-poster-8-portrait.webp' : '/media/hero-poster-8.webp';

const heroLines = [
    'We build software.',
    'We implement AI.',
];

// Paced against shot 1 of the hero loop: the river runs 3.5s, and the CTA
// lands at ~3.4s, so the statement finishes just as the film cuts away.
const HERO_FADE_DURATION = 0.55;
const HERO_PAUSE_BETWEEN = 0.65;
const HERO_INITIAL_DELAY = 0.5;

const heroLineDelays = heroLines.map((_, i) =>
    HERO_INITIAL_DELAY + i * (HERO_FADE_DURATION + HERO_PAUSE_BETWEEN)
);

const totalDuration = heroLineDelays[heroLines.length - 1] + HERO_FADE_DURATION;

const Homepage = () => {
    const heroReady = true; // hero animates in on mount; there is no splash to wait for
    const heroVideoRef = useRef(null);

    // The loop buffers behind the splash but stays paused on frame 0, so it
    // starts on the river shot the headline was timed against.
    useSectionViews(['services', 'story', 'work', 'testimonials', 'about']);

    useEffect(() => {
        const video = heroVideoRef.current;
        if (!heroReady || !video) return;
        if (video.currentTime > 0) video.currentTime = 0;
        const play = video.play();
        if (play) play.catch(() => { /* autoplay refused; poster stands in */ });
    }, [heroReady]);

    useEffect(() => {
        const hash = window.location.hash;
        if (hash) {
            const el = document.getElementById(hash.replace('#', ''));
            if (el) {
                setTimeout(() => {
                    el.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            }
        }
    }, []);

    return (
        <Box>
            <Helmet>
                <title>AI Consulting & Custom Software in Minneapolis | minneAI</title>
                <meta name="description" content="AI consulting and custom software in Minneapolis. We build the software, put AI to work inside it, and connect it to the systems you already use. Free intro call." />
                <link rel="canonical" href="https://minneai.com/" />
                <meta property="og:title" content="AI Consulting & Custom Software in Minneapolis | minneAI" />
                <meta property="og:description" content="AI consulting and custom software in Minneapolis. We build the software, put AI to work inside it, and connect it to the systems you already use. Free intro call." />
                <meta property="og:url" content="https://minneai.com/" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'VideoObject',
                        name: 'How we work — minneAI and WalkPort',
                        description: 'A short film about minneAI\'s work on WalkPort, an FDA-registered walk station. minneAI wrote the firmware that reads the machine\'s sensors and built the app that counts every step.',
                        thumbnailUrl: 'https://minneai.com/media/story-poster.jpg',
                        uploadDate: '2026-08-16T20:00:00-05:00',
                        duration: 'PT2M25S',
                        contentUrl: 'https://minneai.com/media/minneai-story.mp4',
                        url: 'https://minneai.com/#story',
                        publisher: { '@type': 'ProfessionalService', '@id': 'https://minneai.com/#org', name: 'minneAI', logo: { '@type': 'ImageObject', url: 'https://minneai.com/media/minneai-logo-512.png' } },
                    })}
                </script>
            </Helmet>
            <Menu />

            {/* Hero Section */}
            <Box
                sx={{
                    position: 'relative',
                    bgcolor: '#0c0c0c', // what shows if the poster is slow to arrive
                    // svh, not dvh: dvh tracks the mobile URL bar, so the hero
                    // resized live as the bar collapsed and the covered video
                    // re-cropped on every frame of it. svh is the stable
                    // smallest-viewport height, so scrolling changes nothing.
                    height: '100vh',
                    '@supports (height: 100svh)': { height: '100svh' },
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                }}
            >
                {/* No autoPlay: preload="auto" still fetches the file during the
                    splash, but leaving it paused means the first frame anyone
                    sees is the river rather than wherever the loop had run to. */}
                <video
                    ref={heroVideoRef}
                    muted loop playsInline preload={LIGHT_MEDIA ? 'metadata' : 'auto'}
                    poster={HERO_POSTER}
                    style={{
                        position: 'absolute', top: 0, left: 0,
                        width: '100%', height: '100%',
                        objectFit: 'cover', zIndex: 0,
                    }}
                >
                    <source src={HERO_VIDEO} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>

                {/* The loop opens dark on the river and brightens through the
                    interior shots, so the scrim carries more weight than it did
                    over the old plate. */}
                <Box sx={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'linear-gradient(180deg, rgba(0,0,0,0.60) 0%, rgba(0,0,0,0.50) 100%)',
                    zIndex: 1,
                }} />

                {/* Staggered hero content */}
                <Box
                    sx={{
                        zIndex: 2, textAlign: 'center',
                        padding: '0 32px', maxWidth: '700px',
                        pointerEvents: 'none',
                    }}
                >
                    <Box component="h1" sx={{ m: 0, p: 0, mb: 4 }}>
                        {heroLines.map((line, li) => (
                            <motion.span
                                key={li}
                                initial={{ opacity: 0, y: 10 }}
                                animate={heroReady ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                                transition={{
                                    duration: HERO_FADE_DURATION,
                                    delay: heroLineDelays[li],
                                    ease: 'easeOut',
                                }}
                                style={{
                                    display: 'block',
                                    fontWeight: 300,
                                    fontSize: 'clamp(1.8rem, 5vw, 3rem)',
                                    color: 'white',
                                    textShadow: '2px 2px 8px rgba(0,0,0,0.4)',
                                    lineHeight: 1.4,
                                    textAlign: 'center',
                                }}
                            >
                                {line}{' '}
                            </motion.span>
                        ))}
                    </Box>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={heroReady ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                        transition={{ duration: 0.5, delay: totalDuration + HERO_PAUSE_BETWEEN, ease }}
                    >
                        <Button variant="contained" size="large" component={RouterLink} to="/Contact" onClick={() => {
                            track('cta_click', {location: 'hero'});
                        }} sx={{
                            pointerEvents: 'auto',
                            fontWeight: 600, fontSize: { xs: '1rem', sm: '1.1rem' }, whiteSpace: 'nowrap',
                            px: { xs: 3, sm: 4 }, py: 1.5, backgroundColor: colors.green, color: 'white',
                            borderRadius: '8px', boxShadow: '0 4px 20px rgba(22,163,74,0.45)',
                            '&:hover': {
                                backgroundColor: colors.greenDark,
                                boxShadow: '0 6px 24px rgba(22,163,74,0.6)',
                            },
                        }}>
                            Schedule a Free Consultation
                        </Button>
                    </motion.div>
                </Box>

                {/* Scroll indicator */}
                <Box sx={{ position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)', zIndex: 2, display: { xs: 'none', md: 'block' } }}>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={heroReady ? { opacity: 1, y: [0, 12, 0] } : { opacity: 0 }}
                        transition={{ opacity: { duration: 0.5, delay: totalDuration + 1.0 }, y: { repeat: Infinity, duration: 2, ease: 'easeInOut', delay: totalDuration + 1.0 } }}
                    >
                        <ChevronDown size={44} color="rgba(255,255,255,0.55)" />
                    </motion.div>
                </Box>
            </Box>

            <ServicesSection />

            <FilmSection />

            <WorkSection />

            <Testimonials />

            <AboutSection />

            {/* Call To Action. Tinted so it reads as its own section -- it sits
                between a white About and a white footer. The question bookends the
                splash's "I have an idea." rather than repeating its typing effect. */}
            <Box component="section" sx={{ py: { xs: 9, md: 13 }, px: 4, textAlign: 'center', backgroundColor: '#f6f8f7' }}>
                <SectionHeading title="What are you trying to build?" align="center" />
                <Typography variant="h6" component="p" color="text.secondary" fontWeight={400} sx={{ mt: 2.5, mb: 4, maxWidth: 520, mx: 'auto', fontSize: { xs: '1.05rem', md: '1.25rem' } }}>
                    Tell us where you're starting from — we'll tell you what it would take.
                </Typography>
                <Button variant="contained" size="large" component={RouterLink} to="/Contact" onClick={() => {
                    track('cta_click', {location: 'page_bottom'});
                }} sx={{
                    fontWeight: 600, fontSize: '1.1rem', px: 4, py: 1.5,
                    borderRadius: '8px', backgroundColor: colors.green,
                    boxShadow: '0 4px 20px rgba(22,163,74,0.3)',
                    '&:hover': {
                        backgroundColor: colors.greenDark,
                        boxShadow: '0 6px 24px rgba(22,163,74,0.45)',
                    },
                }}>
                    Schedule a Free Consultation
                </Button>
            </Box>

            <ChatAssistant />

            <Footer />
        </Box>
    );
};

export default Homepage;
